// src/components/chat/UserDetailsPanel.js
import { doc, updateDoc, arrayUnion, setDoc, serverTimestamp } from 'firebase/firestore';
import { getDisplayName, getUserAvatar } from '../../hooks/displayNameHelper';
import React, { useEffect, useState } from 'react';
import { db, auth } from '../../firebase';
import { X, MessageSquare, Shield } from 'lucide-react';
import { useUserOnlineStatus } from '../../hooks/useOnlineStatus';
import { watchUserProfile } from '../../hooks/userProfiles';
import toast from 'react-hot-toast';
import { useIdGenerator } from '../../hooks/useidgenerator';

const UserDetailsPanel = ({ 
  userId, 
  onClose, 
  channelData, 
  setCustomActiveChannel,
  channels,
  profile
}) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isOnline = useUserOnlineStatus(userId);
  const currentUserId = auth.currentUser?.uid;
  const isAdmin = channelData?.createdBy?.includes(currentUserId);
  const { generateChatId } = useIdGenerator()

  useEffect(() => {
    if (!userId) return;

    // Live while the rules allow it, one-shot when they do not: a chat member
    // from an unbound organisation is exactly what getUserProfiles is for.
    return watchUserProfile(userId, (user) => {
      if (user) setUserData(user);
      setLoading(false);
    });
  }, [userId]);

  const handlePromoteToAdmin = async () => {
    try {
      if (!channelData?.id || !userId) return;
      
      const channelRef = doc(db, 'chats', channelData.id);
      await updateDoc(channelRef, {
        subAdmins: arrayUnion(userId), // Add to subAdmins array instead of createdBy
        lastUpdatedAt: serverTimestamp()
      });
      toast.success('User promoted to admin successfully');
    } catch (error) {
      console.error('Error promoting user to admin:', error);
      toast.error('Failed to promote user to admin');
    }
  };

  const handleStartMessage = async () => {
    if (!userData) return;
    
    // Check if a P2P chat already exists with this user
    const existingP2PChat = channels.find(chat => {
      return chat.chatType === 'p2p' && 
             chat.members.some(m => m.userId === currentUserId) && 
             chat.members.some(m => m.userId === userId);
    });

    if (existingP2PChat) {
      setCustomActiveChannel(existingP2PChat.id);
      onClose();
    } else {
      try {
        // Create a new P2P chat
        const chatId = generateChatId();
        const members = [
          {
            userId: currentUserId,
            displayName: profile?.displayName?.stringValue || profile?.fullname?.stringValue || `User_${currentUserId.slice(0, 6)}`,
            profilePic: getUserAvatar({ ...profile, profileUrl: profile?.profileUrl?.stringValue, displayName: profile?.displayName?.stringValue || profile?.fullname?.stringValue }),
            addedBy: null,
            // isOnline: true
          },
          {
            userId: userId,
            displayName: userData.displayName || userData?.fullname || `User_${userId.slice(0, 6)}`,
            profilePic: getUserAvatar(userData),
            addedBy: currentUserId,
            // isOnline: isOnline
          }
        ];

        await setDoc(doc(db, 'chats', chatId), {
          chatType: 'p2p',
          createdAt: serverTimestamp(),
          createdBy: [currentUserId],
          groupName: null,
          id: chatId,
          lastMessage: null,
          lastMessageId: null,
          lastMessageSenderId: null,
          lastUpdatedAt: serverTimestamp(),
          members,
          mutedBy: [],
          pinnedMessages: [],
          profileBgColor: "#ffd99489",
          typingStatus: {},
        });
        
        setCustomActiveChannel(chatId);
        onClose();
      } catch (error) {
        console.error('Error creating new chat:', error);
        toast.error('Failed to start new conversation');
      }
    }
  };

  if (!userId) return null;

  return (
    <div className="p-2 space-y-4 bg-gray-50 min-h-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">User Details</h2>
        <button 
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : userData ? (
        <div className="space-y-6">
          <div className="flex flex-col items-center">
            <div className="relative w-24 h-24 rounded-full bg-gray-200 mb-4 overflow-hidden">
              <img
                src={getUserAvatar(userData)}
                alt={userData.displayName || 'User'}
                className="w-full h-full object-cover"
              />
              <div className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-white ${
                isOnline ? 'bg-green-500' : 'bg-gray-400'
              }`} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              {userData.displayName || userData.name || `User_${userId.slice(0, 6)}`}
            </h3>
            {userData.email && (
              <p className="text-gray-500">{userData.email}</p>
            )}
            <p className={`text-sm ${isOnline ? 'text-green-500' : 'text-gray-500'}`}>
              {isOnline ? 'Online' : 'Offline'}
            </p>
          </div>

          {isAdmin && userId !== currentUserId && (
            <div className="flex justify-center space-x-4">
              <button
                onClick={handlePromoteToAdmin}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
                disabled={channelData?.subAdmins?.includes(userId) || channelData?.createdBy?.includes(userId)}
              >
                <Shield className="w-4 h-4" />
                <span>
                  {channelData?.createdBy?.includes(userId) ? 'Creator Admin' : 
                   channelData?.subAdmins?.includes(userId) ? 'Already Admin' : 'Promote to Admin'}
                </span>
              </button>
              <button
                onClick={handleStartMessage}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 flex items-center space-x-2"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Message</span>
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <h4 className="text-sm font-medium text-gray-500 mb-2">User ID</h4>
              <p className="text-sm text-gray-900 break-all">{userId}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-500 py-12">
          <p>User not found</p>
        </div>
      )}
    </div>
  );
}

export default UserDetailsPanel;